import React, { useRef, useState } from "react";
import {
  FileText, Upload, Trash2, Eye, Sparkles, Loader2, History,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  Callout, ConfirmModal, DropZone, FileViewerModal, EmptyState,
} from "../ui";
import ResumeReport, { ScorePill } from "./ResumeReport";
import { ANALYSIS_ERROR_HELP, fmtDate } from "./constants";
import {
  uploadFile, saveResume, removeResume, analyzeResume, fetchMyAnalyses,
  errorText, errorCode,
} from "./portfolioApi";

/**
 * ResumeCard — upload a resume, score it, read the report, compare with earlier runs.
 *
 * ⚠️ THE ANALYSIS IS A LONG SYNCHRONOUS CALL, on purpose. A reasoning model
 * reading a two-page CV takes 20-60 seconds; a queue plus polling would add a
 * second failure mode and a "still working…" state that is harder to trust than
 * a button that is visibly busy. `analyzeResume` in portfolioApi carries the
 * extended timeout that makes this safe.
 *
 * ⚠️ Uploading a NEW file clears the old score server-side, and the UI must show
 * that immediately. A number left on screen that describes the document the
 * student has just replaced is worse than no number.
 */

const ACCEPT = ".pdf,.docx,.doc,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const MAX_BYTES = 10 * 1024 * 1024;

const prettySize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ResumeCard = ({ resume, latestAnalysis, onPortfolioChange, onAnalysed }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [msg, setMsg] = useState({ tone: "", text: "" });
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [viewing, setViewing] = useState(false);

  const [history, setHistory] = useState(null);   // null = not loaded yet
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const hasResume = !!resume?.key;

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileRef.current) fileRef.current.value = "";

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      setMsg({ tone: "danger", text: "Upload a PDF, DOC or DOCX file." });
      return;
    }
    if (file.size > MAX_BYTES) {
      setMsg({ tone: "danger", text: "Your resume must be smaller than 10 MB." });
      return;
    }

    setUploading(true);
    setMsg({ tone: "", text: "" });
    try {
      const { key } = await uploadFile(file);
      const { portfolio } = await saveResume({
        key, fileName: file.name, mimeType: file.type, size: file.size,
      });
      // The server has already cleared the previous score; clearing it here too
      // keeps the two in step without a refetch.
      onPortfolioChange(portfolio);
      onAnalysed(null);
      setHistory(null);
      setMsg({ tone: "success", text: "Resume uploaded. Run the analysis when you are ready." });
    } catch (err) {
      setMsg({ tone: "danger", text: errorText(err, "Failed to upload your resume.") });
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const { portfolio } = await removeResume();
      onPortfolioChange(portfolio);
      onAnalysed(null);
      setHistory(null);
      setConfirmRemove(false);
      setMsg({ tone: "success", text: "Resume removed." });
    } catch (err) {
      setMsg({ tone: "danger", text: errorText(err, "Failed to remove your resume.") });
    } finally {
      setRemoving(false);
    }
  };

  const analyse = async () => {
    setAnalysing(true);
    setMsg({ tone: "", text: "" });
    try {
      const { analysis } = await analyzeResume();
      onAnalysed(analysis);
      setHistory(null);   // a new run invalidates the loaded history
      setMsg({ tone: "success", text: "Analysis complete." });
    } catch (err) {
      // A parse failure and a provider outage are different dead ends and get
      // different instructions — see ANALYSIS_ERROR_HELP.
      const code = errorCode(err);
      setMsg({ tone: "danger", text: ANALYSIS_ERROR_HELP[code] || errorText(err, "The analysis could not be completed.") });
    } finally {
      setAnalysing(false);
    }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) {
      setHistoryLoading(true);
      try {
        const { analyses } = await fetchMyAnalyses();
        setHistory(analyses || []);
      } catch {
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <FileText size={18} /> Resume
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            Upload your CV and have it scored against the role in your career profile.
          </p>
        </div>
        {latestAnalysis?.status === "completed" && (
          <ScorePill score={latestAnalysis.overallScore} />
        )}
      </AppCardHeader>

      <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="tw-hidden"
          onChange={onFile}
        />

        {msg.text && (
          <Callout tone={msg.tone === "success" ? "success" : "danger"} role="status">
            {msg.text}
          </Callout>
        )}

        {!hasResume ? (
          <DropZone size="lg" onClick={pickFile} disabled={uploading}>
            {uploading ? (
              <Loader2 size={26} className="tw-animate-spin tw-text-primary" />
            ) : (
              <Upload size={26} className="tw-text-content-subtle" />
            )}
            <span className="tw-text-sm tw-font-semibold tw-text-content">
              {uploading ? "Uploading…" : "Upload your resume"}
            </span>
            <span className="tw-text-xs tw-text-content-subtle">PDF, DOC or DOCX · up to 10 MB</span>
          </DropZone>
        ) : (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3.5">
            <span className="tw-grid tw-h-11 tw-w-11 tw-shrink-0 tw-place-items-center tw-rounded-sm tw-bg-primary/10 tw-text-primary">
              <FileText size={20} />
            </span>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-content">
                {resume.fileName || "Resume"}
              </p>
              <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
                {[prettySize(resume.size), resume.uploadedAt && `Uploaded ${fmtDate(resume.uploadedAt)}`]
                  .filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              <AppButton variant="outline" size="sm" onClick={() => setViewing(true)} disabled={!resume.url}>
                <Eye size={14} /> View
              </AppButton>
              <AppButton variant="outline" size="sm" onClick={pickFile} disabled={uploading}>
                <Upload size={14} /> {uploading ? "Uploading…" : "Replace"}
              </AppButton>
              <AppButton variant="ghost-destructive" size="sm" onClick={() => setConfirmRemove(true)} disabled={uploading}>
                <Trash2 size={14} /> Remove
              </AppButton>
            </div>
          </div>
        )}

        {hasResume && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <AppButton onClick={analyse} disabled={analysing}>
              {analysing
                ? <><Loader2 size={14} className="tw-animate-spin" /> Analysing…</>
                : <><Sparkles size={14} /> {latestAnalysis ? "Re-run analysis" : "Analyse my resume"}</>}
            </AppButton>
            <AppButton variant="ghost" size="sm" onClick={toggleHistory}>
              <History size={14} /> Past scores
              {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </AppButton>
            {analysing && (
              <span className="tw-text-xs tw-text-content-subtle">
                This takes up to a minute. Leave the page open.
              </span>
            )}
          </div>
        )}

        {showHistory && (
          <HistoryList rows={history} loading={historyLoading} />
        )}

        {latestAnalysis && !analysing && (
          <div className="tw-mt-1 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-5">
            <ResumeReport analysis={latestAnalysis} />
          </div>
        )}

        {hasResume && !latestAnalysis && !analysing && (
          <EmptyState
            icon={Sparkles}
            title="Not analysed yet"
            description="Run the analysis to see how your resume scores for the role you are targeting, and exactly what to change."
          />
        )}
      </AppCardContent>

      <ConfirmModal
        show={confirmRemove}
        onHide={() => !removing && setConfirmRemove(false)}
        onConfirm={remove}
        loading={removing}
        variant="danger"
        title="Remove your resume?"
        message="Your uploaded file and its score will be deleted. Past analysis reports stay in your history."
        confirmText="Remove"
      />

      <FileViewerModal
        show={viewing}
        onHide={() => setViewing(false)}
        fileUrl={resume?.url || ""}
        fileName={resume?.fileName || "Resume"}
        fileType={resume?.mimeType || "application/pdf"}
      />
    </AppCard>
  );
};

/**
 * Past runs, newest first, each showing the move against the one before it.
 *
 * The delta is the reason the history exists — "81, up 13 since Tuesday" is what
 * tells a student their rewrite worked. A list of bare numbers would not.
 */
const HistoryList = ({ rows, loading }) => {
  if (loading) {
    return <p className="tw-m-0 tw-text-sm tw-text-content-subtle">Loading past scores…</p>;
  }
  const completed = (rows || []).filter((r) => r.status === "completed");
  if (!completed.length) {
    return <p className="tw-m-0 tw-text-sm tw-text-content-subtle">No earlier analyses yet.</p>;
  }

  return (
    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
      {completed.map((r, i) => {
        // Rows are newest-first, so the previous run is the NEXT index.
        const prev = completed[i + 1];
        const delta = prev ? r.overallScore - prev.overallScore : null;
        const Trend = delta === null ? null : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
        const trendClass = delta > 0 ? "tw-text-success" : delta < 0 ? "tw-text-danger" : "tw-text-content-subtle";
        return (
          <li
            key={r._id}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-px-3 tw-py-2"
          >
            <span className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-content">
              {r.overallScore}
            </span>
            {Trend && delta !== 0 && (
              <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-xs tw-font-medium ${trendClass}`}>
                <Trend size={13} />{delta > 0 ? `+${delta}` : delta}
              </span>
            )}
            <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-text-content-muted">
              {r.targetRole || "General"}
            </span>
            <span className="tw-text-xs tw-text-content-subtle">
              {fmtDate(r.completedAt || r.createdAt)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default ResumeCard;
